import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth'
import ModelUploader, { type UploadedFileInfo } from '../../components/admin/ModelUploader'
import ThumbnailUploader from '../../components/admin/ThumbnailUploader'
import { modelsApi } from '../../services/api/models'

interface ModelFormData {
  name: string
  description: string
  category: string
  tags: string[]
  visibility: 'public' | 'private' | 'unlisted'
  featured: boolean
  vertices?: number
  duration?: number
}

export default function NewModel() {
  const navigate = useNavigate()
  const { token, adminEmail } = useAuth()
  const [uploadId, setUploadId] = useState<string>('')
  const [modelId, setModelId] = useState<string>('')
  const [fileInfo, setFileInfo] = useState<{ filename: string; fileSize: number } | null>(null)
  // Metadata the uploader read from the file (a .4dv's { fourdv: stats }),
  // sent with the create request.
  const [uploadMetadata, setUploadMetadata] = useState<Record<string, unknown> | undefined>()
  const [tagInput, setTagInput] = useState('')
  
  const [formData, setFormData] = useState<ModelFormData>({
    name: '',
    description: '',
    category: '',
    tags: [],
    visibility: 'public',
    featured: false
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => modelsApi.getCategories()
  })

  const createModelMutation = useMutation({
    mutationFn: async (data: ModelFormData & { uploadId: string; metadata?: Record<string, unknown> }) => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to create model')
      return response.json()
    },
    onSuccess: (data) => {
      setModelId(data.data.id)
    }
  })

  const handleUploadComplete = (uploadId: string, fileInfo: UploadedFileInfo) => {
    setUploadId(uploadId)
    setFileInfo(fileInfo)
    setUploadMetadata(fileInfo.metadata)
    // Pre-fill what the uploader read from the file itself (whole seconds in the
    // form; for a .4dv the server stores the exact metadata.fourdv.durationSec).
    const { vertices, duration } = fileInfo
    setFormData(prev => ({
      ...prev,
      ...(vertices !== undefined ? { vertices } : {}),
      ...(duration !== undefined && duration > 0 ? { duration: Math.max(1, Math.round(duration)) } : {}),
    }))
    // Auto-fill name from filename if empty
    if (!formData.name) {
      const name = fileInfo.filename.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
      setFormData(prev => ({ ...prev, name }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!uploadId) {
      alert('Please upload a model file first')
      return
    }

    await createModelMutation.mutateAsync({
      ...formData,
      uploadId,
      ...(uploadMetadata ? { metadata: uploadMetadata } : {})
    })
  }

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }))
      setTagInput('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }))
  }

  const categories = categoriesData?.data || []

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin/models')}
                className="mr-4 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Add New Model</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Model File</h2>
            <ModelUploader onUploadComplete={handleUploadComplete} />
            {fileInfo && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  File uploaded: <span className="font-medium">{fileInfo.filename}</span> ({(fileInfo.fileSize / (1024 * 1024)).toFixed(2)} MB)
                </p>
              </div>
            )}
          </div>

          {/* Model Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Model Details</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  id="category"
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="vertices" className="block text-sm font-medium text-gray-700 mb-1">
                    Vertices (optional)
                  </label>
                  <input
                    id="vertices"
                    type="number"
                    value={formData.vertices || ''}
                    onChange={(e) => setFormData({ ...formData, vertices: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (seconds)
                  </label>
                  <input
                    id="duration"
                    type="number"
                    step="any"
                    min="0"
                    value={formData.duration || ''}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Visibility Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Visibility Settings</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visibility
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="public"
                      checked={formData.visibility === 'public'}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as ModelFormData['visibility'] })}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Public</span> - Anyone can view and download
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="unlisted"
                      checked={formData.visibility === 'unlisted'}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as ModelFormData['visibility'] })}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Unlisted</span> - Only accessible with direct link
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="private"
                      checked={formData.visibility === 'private'}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as ModelFormData['visibility'] })}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Private</span> - Only specific users can access
                    </span>
                  </label>
                </div>
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">
                  <span className="font-medium">Featured</span> - Display prominently in gallery
                </span>
              </label>
            </div>
          </div>

          {/* Thumbnail Upload - Show after model is created */}
          {modelId && (
            <div className="bg-white rounded-lg shadow p-6">
              <ThumbnailUploader 
                modelId={modelId}
                onUploadComplete={() => {
                  navigate('/admin/models')
                }}
              />
              <p className="text-sm text-gray-500 mt-2">
                You can skip this step and add a thumbnail later
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/models')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            {!modelId ? (
              <button
                type="submit"
                disabled={!uploadId || createModelMutation.isPending}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                {createModelMutation.isPending ? 'Creating...' : 'Create Model'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/admin/models')}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Finish
              </button>
            )}
          </div>
        </form>
      </main>
    </div>
  )
}